import React from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import { CachedRecordingItem } from 'store/recordings/reducer.types';

import {
    hasSeriesRecording,
    isFailed,
    isProtectedRecording,
    isRecorded,
    isRecording,
    isScheduled,
} from 'utils/recordings';
import { isWeb } from 'utils/responsiveness';

import FailedSeriesRecording from './FailedSeriesRecording';
import FailedSingleRecording from './FailedSingleRecording';
import OngoingSeriesRecording from './OngoingSeriesRecording';
import OngoingSingleRecording from './OngoingSingleRecording';
import ProtectedRecording from './ProtectedRecording';
import RecordedSeriesRecording from './RecordedSeriesRecording';
import RecordedSingleRecording from './RecordedSingleRecording';
import styles from './styles';

type Props = {
    highlighted?: boolean;
    size?: number;
    recordingItem?: CachedRecordingItem;
    style?: {};
    collectionSuspensionActive: boolean | undefined;
    tabIndex?: 0 | -1;
};

const {
    ACCESSIBILITY: {
        DETAIL_PAGE: {
            ACCESSIBILITY_ICONS_FAILED_RECORDING,
            ACCESSIBILITY_ICONS_PROTECTED_RECORDING,
            ACCESSIBILITY_ICONS_SCHEDULED_SERIES_RECORDING,
            ACCESSIBILITY_ICONS_SCHEDULED_SINGLE_RECORDING,
            ACCESSIBILITY_ICONS_SERIES_RECORDING,
            ACCESSIBILITY_ICONS_SINGLE_RECORDING,
        },
    },
} = LANGUAGE_KEYS;

export default function RecordingIcon({
    highlighted = false,
    size = 24,
    recordingItem,
    style,
    collectionSuspensionActive,
    tabIndex,
}: Props) {
    const { t } = useTranslation();

    if (isFailed(recordingItem) || collectionSuspensionActive) {
        return (
            <View
                style={[styles.iconContainer, style]}
                accessibilityLabel={t(ACCESSIBILITY_ICONS_FAILED_RECORDING)}
                accessibilityRole="image"
                accessible
                {...(isWeb && { tabIndex })}
            >
                {hasSeriesRecording(recordingItem) ? (
                    <FailedSeriesRecording
                        highlighted={highlighted}
                        size={size}
                    />
                ) : (
                    <FailedSingleRecording
                        highlighted={highlighted}
                        size={size}
                    />
                )}
            </View>
        );
    }

    if (isProtectedRecording(recordingItem)) {
        return (
            <View
                style={[styles.iconContainer, style]}
                accessibilityLabel={t(ACCESSIBILITY_ICONS_PROTECTED_RECORDING)}
                accessibilityRole="image"
                {...(isWeb && { tabIndex })}
                accessible
            >
                <ProtectedRecording highlighted={highlighted} size={size} />
            </View>
        );
    }

    if (isRecorded(recordingItem)) {
        if (hasSeriesRecording(recordingItem)) {
            return (
                <View
                    style={[styles.iconContainer, style]}
                    accessibilityLabel={t(ACCESSIBILITY_ICONS_SERIES_RECORDING)}
                    accessibilityRole="image"
                    // eslint-disable-next-line react/jsx-props-no-spreading, no-undef
                    {...(isWeb && { tabIndex })}
                    accessible
                >
                    <RecordedSeriesRecording
                        highlighted={highlighted}
                        size={size}
                    />
                </View>
            );
        }

        return (
            <View
                style={[styles.iconContainer, style]}
                accessibilityLabel={t(ACCESSIBILITY_ICONS_SINGLE_RECORDING)}
                accessibilityRole="image"
                {...(isWeb && { tabIndex })}
                accessible
            >
                <RecordedSingleRecording
                    highlighted={highlighted}
                    size={size}
                />
            </View>
        );
    }

    if (isScheduled(recordingItem) || isRecording(recordingItem)) {
        if (hasSeriesRecording(recordingItem)) {
            return (
                <View
                    style={[styles.iconContainer, style]}
                    accessibilityLabel={t(
                        ACCESSIBILITY_ICONS_SCHEDULED_SERIES_RECORDING,
                    )}
                    accessibilityRole="image"
                    // eslint-disable-next-line react/jsx-props-no-spreading, no-undef
                    {...(isWeb && { tabIndex })}
                    accessible
                >
                    <OngoingSeriesRecording
                        highlighted={highlighted}
                        size={size}
                    />
                </View>
            );
        }

        return (
            <View
                style={[styles.iconContainer, style]}
                accessibilityLabel={t(
                    ACCESSIBILITY_ICONS_SCHEDULED_SINGLE_RECORDING,
                )}
                accessibilityRole="image"
                {...(isWeb && { tabIndex })}
                accessible
            >
                <OngoingSingleRecording highlighted={highlighted} size={size} />
            </View>
        );
    }

    return null;
}
